import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface DashboardSummary {
  activeClients: number;
  recentClients: number;
  activeLeads: number;
  openLeads: number;
  highPriorityLeads: number;
  activeCases: number;
  inProgressCases: number;

  overdueDeadlines: number;
  todayDeadlines: number;
  upcomingDeadlines: number;

  pendingTasks: number;
  overdueTasks: number;
  completedTasks: number;
  contractedRevenue: number;
  receivedRevenue: number;
  pendingRevenue: number;
  overdueRevenue: number;
  overdueInstallmentsCount: number;
}

export interface DashboardLeadSource {
  source: string;
  count: number;
}

export interface DashboardLeadItem {
  id: string;
  name: string;
  source: string | null;
  status: string;
  priority: string;
  legalProductId: string | null;
  legalProductName: string | null;
  lastContactAt: string | null;
  nextContactAt: string | null;
  createdAt: string;
}

export interface DashboardClientItem {
  id: string;
  name: string;
  city: string | null;
  responsibleUser: string | null;
  status: string | null;
  createdAt: string;
}

export interface DashboardCaseItem {
  id: string;
  title: string;
  clientName: string | null;
  legalProductId: string | null;
  legalProductName: string | null;
  status: string;
  priority: string;
  nextDeadlineAt: string | null;
  updatedAt: string;
}

export interface DashboardDeadlineItem {
  id: string;
  title: string;

  caseId: string;
  caseTitle: string | null;

  clientId: string | null;
  clientName: string | null;

  dueDate: string | null;

  status: string;
  riskLevel: string;

  responsibleUser: string | null;

  taskId: string | null;

  legalBasis: string | null;
}

export interface DashboardTaskItem {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate: string | null;
  responsibleUser: string | null;
  clientName: string | null;
  caseTitle: string | null;
}

export interface DashboardTaskByResponsible {
  name: string;
  total: number;
  completed: number;
}

export interface DashboardInstallmentItem {
  id: string;
  paymentId: string;
  installmentNumber: number;
  amount: number;
  dueDate: string | null;
  status: string;
  paymentTitle: string;
  clientName: string | null;
  caseTitle: string | null;
  paymentStatus: string;
}

export interface DashboardPaymentItem {
  id: string;
  clientId: string;
  clientName: string | null;
  caseId: string | null;
  caseTitle: string | null;
  title: string;
  totalAmount: number;
  paidAmount: number | null;
  balanceAmount: number | null;
  status: string;
  firstDueDate: string | null;
  createdAt: string;
}

export interface DashboardData {
  aggregation: {
    complete: boolean;
    warning?: string;
    failedSources: string[];
  };
  summary: DashboardSummary;
  leadSources: DashboardLeadSource[];
  recentLeads: DashboardLeadItem[];
  recentClients: DashboardClientItem[];
  upcomingDeadlines: DashboardDeadlineItem[];
  activeCases: DashboardCaseItem[];
  pendingTasks: DashboardTaskItem[];
  tasksByResponsible: DashboardTaskByResponsible[];
  upcomingInstallments: DashboardInstallmentItem[];
  recentPayments: DashboardPaymentItem[];
  generatedAt: string;
}

type UseDashboardOptions = {
  autoLoad?: boolean;
};

const emptySummary: DashboardSummary = {
  activeClients: 0,
  recentClients: 0,
  activeLeads: 0,
  openLeads: 0,
  highPriorityLeads: 0,
  activeCases: 0,
  inProgressCases: 0,

  overdueDeadlines: 0,
  todayDeadlines: 0,
  upcomingDeadlines: 0,

  pendingTasks: 0,
  overdueTasks: 0,
  completedTasks: 0,
  contractedRevenue: 0,
  receivedRevenue: 0,
  pendingRevenue: 0,
  overdueRevenue: 0,
  overdueInstallmentsCount: 0,
};

const emptyDashboard: DashboardData = {
  aggregation: { complete: true, failedSources: [] },
  summary: emptySummary,
  leadSources: [],
  recentLeads: [],
  recentClients: [],
  upcomingDeadlines: [],
  activeCases: [],
  pendingTasks: [],
  tasksByResponsible: [],
  upcomingInstallments: [],
  recentPayments: [],
  generatedAt: new Date().toISOString(),
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return 0;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeDashboard = (payload: any): DashboardData => {
  const dashboard = payload?.dashboard ?? payload ?? {};
  const summary = dashboard.summary ?? {};

  return {
    aggregation: {
      complete: payload?.aggregation?.complete !== false,
      warning: toNullableText(payload?.aggregation?.warning) ?? undefined,
      failedSources: Array.isArray(payload?.aggregation?.failedSources) ? payload.aggregation.failedSources.map(String) : [],
    },
    summary: {
      activeClients: toNumber(summary.activeClients),
      recentClients: toNumber(summary.recentClients),
      activeLeads: toNumber(summary.activeLeads),
      openLeads: toNumber(summary.openLeads),
      highPriorityLeads: toNumber(summary.highPriorityLeads),
      activeCases: toNumber(summary.activeCases),
      inProgressCases: toNumber(summary.inProgressCases),

      overdueDeadlines:
        toNumber(
          summary.overdueDeadlines
        ),

      todayDeadlines:
        toNumber(
          summary.todayDeadlines
        ),

      upcomingDeadlines:
        toNumber(
          summary.upcomingDeadlines
        ),

      pendingTasks: toNumber(summary.pendingTasks),
      overdueTasks: toNumber(summary.overdueTasks),
      completedTasks: toNumber(summary.completedTasks),
      contractedRevenue: toNumber(summary.contractedRevenue),
      receivedRevenue: toNumber(summary.receivedRevenue),
      pendingRevenue: toNumber(summary.pendingRevenue),
      overdueRevenue: toNumber(summary.overdueRevenue),
      overdueInstallmentsCount: toNumber(summary.overdueInstallmentsCount),
    },
    leadSources: Array.isArray(dashboard.leadSources)
      ? dashboard.leadSources.map((item: any) => ({
          source: toNullableText(item?.source) ?? 'Sem origem',
          count: toNumber(item?.count),
        }))
      : [],
    recentLeads: Array.isArray(dashboard.recentLeads)
      ? dashboard.recentLeads.map((item: any) => ({
          id: String(item?.id ?? ''),
          name: toNullableText(item?.name) ?? 'Sem nome',
          source: toNullableText(item?.source),
          status: String(item?.status ?? 'new'),
          priority: String(item?.priority ?? 'normal'),
          legalProductId: toNullableText(item?.legalProductId ?? item?.legal_product_id),
          legalProductName: toNullableText(item?.legalProductName ?? item?.legal_product_name),
          lastContactAt: toNullableText(item?.lastContactAt ?? item?.last_contact_at),
          nextContactAt: toNullableText(item?.nextContactAt ?? item?.next_contact_at),
          createdAt: toNullableText(item?.createdAt ?? item?.created_at) ?? new Date().toISOString(),
        }))
      : [],
    recentClients: Array.isArray(dashboard.recentClients)
      ? dashboard.recentClients.map((item: any) => ({
          id: String(item?.id ?? ''),
          name: toNullableText(item?.name) ?? 'Sem nome',
          city: toNullableText(item?.city),
          responsibleUser: toNullableText(item?.responsibleUser ?? item?.responsible_user),
          status: toNullableText(item?.status),
          createdAt: toNullableText(item?.createdAt ?? item?.created_at) ?? new Date().toISOString(),
        }))
      : [],
    upcomingDeadlines: Array.isArray(dashboard.upcomingDeadlines)
      ? dashboard.upcomingDeadlines.map((item: any) => ({
          id:
            String(
              item?.id ??
              ''
            ),

          title:
            toNullableText(
              item?.title
            ) ??
            'Prazo sem título',

          caseId:
            String(
              item?.caseId ??
              item?.case_id ??
              ''
            ),

          caseTitle:
            toNullableText(
              item?.caseTitle ??
              item?.case_title
            ),

          clientId:
            toNullableText(
              item?.clientId ??
              item?.client_id
            ),

          clientName:
            toNullableText(
              item?.clientName ??
              item?.client_name
            ),

          dueDate:
            toNullableText(
              item?.dueDate ??
              item?.due_date
            ),

          status:
            String(
              item?.status ??
              'pending'
            ),

          riskLevel:
            String(
              item?.riskLevel ??
              item?.risk_level ??
              'medium'
            ),

          responsibleUser:
            toNullableText(
              item?.responsibleUser ??
              item?.responsible_user
            ),

          taskId:
            toNullableText(
              item?.taskId ??
              item?.task_id
            ),

          legalBasis:
            toNullableText(
              item?.legalBasis ??
              item?.legal_basis
            ),
        }))
      : [],
    activeCases: Array.isArray(dashboard.activeCases)
      ? dashboard.activeCases.map((item: any) => ({
          id: String(item?.id ?? ''),
          title: toNullableText(item?.title) ?? 'Sem título',
          clientName: toNullableText(item?.clientName ?? item?.client_name),
          legalProductId: toNullableText(item?.legalProductId ?? item?.legal_product_id),
          legalProductName: toNullableText(item?.legalProductName ?? item?.legal_product_name),
          status: String(item?.status ?? 'active'),
          priority: String(item?.priority ?? 'normal'),
          nextDeadlineAt: toNullableText(item?.nextDeadlineAt ?? item?.next_deadline_at),
          updatedAt: toNullableText(item?.updatedAt ?? item?.updated_at) ?? new Date().toISOString(),
        }))
      : [],
    pendingTasks: Array.isArray(dashboard.pendingTasks)
      ? dashboard.pendingTasks.map((item: any) => ({
          id: String(item?.id ?? ''),
          title: toNullableText(item?.title) ?? 'Sem título',
          status: String(item?.status ?? 'pending'),
          priority: String(item?.priority ?? 'normal'),
          dueDate: toNullableText(item?.dueDate ?? item?.due_date),
          responsibleUser: toNullableText(item?.responsibleUser ?? item?.responsible_user),
          clientName: toNullableText(item?.clientName ?? item?.client_name),
          caseTitle: toNullableText(item?.caseTitle ?? item?.case_title),
        }))
      : [],
    tasksByResponsible: Array.isArray(dashboard.tasksByResponsible)
      ? dashboard.tasksByResponsible.map((item: any) => ({
          name: toNullableText(item?.name) ?? 'Sem responsável',
          total: toNumber(item?.total),
          completed: toNumber(item?.completed),
        }))
      : [],
    upcomingInstallments: Array.isArray(dashboard.upcomingInstallments)
      ? dashboard.upcomingInstallments.map((item: any) => ({
          id: String(item?.id ?? ''),
          paymentId: String(item?.paymentId ?? item?.payment_id ?? ''),
          installmentNumber: toNumber(item?.installmentNumber ?? item?.installment_number),
          amount: toNumber(item?.amount),
          dueDate: toNullableText(item?.dueDate ?? item?.due_date),
          status: String(item?.status ?? 'pending'),
          paymentTitle: toNullableText(item?.paymentTitle ?? item?.payment_title) ?? 'Sem título',
          clientName: toNullableText(item?.clientName ?? item?.client_name),
          caseTitle: toNullableText(item?.caseTitle ?? item?.case_title),
          paymentStatus: String(item?.paymentStatus ?? item?.payment_status ?? 'pending'),
        }))
      : [],
    recentPayments: Array.isArray(dashboard.recentPayments)
      ? dashboard.recentPayments.map((item: any) => ({
          id: String(item?.id ?? ''),
          clientId: String(item?.clientId ?? item?.client_id ?? ''),
          clientName: toNullableText(item?.clientName ?? item?.client_name),
          caseId: toNullableText(item?.caseId ?? item?.case_id),
          caseTitle: toNullableText(item?.caseTitle ?? item?.case_title),
          title: toNullableText(item?.title) ?? 'Sem título',
          totalAmount: toNumber(item?.totalAmount ?? item?.total_amount),
          paidAmount: toNullableText(item?.paidAmount ?? item?.paid_amount) ? toNumber(item?.paidAmount ?? item?.paid_amount) : null,
          balanceAmount: toNullableText(item?.balanceAmount ?? item?.balance_amount)
            ? toNumber(item?.balanceAmount ?? item?.balance_amount)
            : null,
          status: String(item?.status ?? 'pending'),
          firstDueDate: toNullableText(item?.firstDueDate ?? item?.first_due_date),
          createdAt: toNullableText(item?.createdAt ?? item?.created_at) ?? new Date().toISOString(),
        }))
      : [],
    generatedAt: toNullableText(dashboard.generatedAt) ?? new Date().toISOString(),
  };
};

export function useDashboard(options: UseDashboardOptions = {}) {
  const { autoLoad = true } = options;
  const [dashboard, setDashboard] = useState<DashboardData>(emptyDashboard);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadDashboard = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoadingDashboard(true);
    setDashboardError(null);

    try {
      const response = await fetchWithRetry('/api/dashboard');
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || `GET /api/dashboard falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        const nextDashboard = normalizeDashboard(data);
        setDashboard(nextDashboard);
        setDashboardError(nextDashboard.aggregation.complete ? null : nextDashboard.aggregation.warning ?? 'Consolidação parcial dos indicadores.');
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard real. Mantendo indicadores vazios.', error);

      if (requestId === requestIdRef.current) {
        setDashboardError('Não foi possível carregar o dashboard. Os dados anteriores foram preservados.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingDashboard(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadDashboard();
  }, [autoLoad, loadDashboard]);

  return {
    dashboard,
    isLoadingDashboard,
    dashboardError,
    loadDashboard,
  };
}
